import type { CellFormat } from '../compositor/GridController'

export type SpreadsheetCommandSource = 'ribbon' | 'keyboard' | 'context-menu' | 'status-bar'

export type SpreadsheetCommandState = {
  enabled: boolean
  active?: boolean
  visible?: boolean
  disabledReason?: string
}

export type SpreadsheetCommandId =
  | 'undo'
  | 'redo'
  | 'save'
  | 'paste'
  | 'paste-values'
  | 'paste-formulas'
  | 'paste-formats'
  | 'paste-transpose'
  | 'fill-copy-cells'
  | 'fill-series'
  | 'fill-days'
  | 'fill-weekdays'
  | 'fill-months'
  | 'fill-years'
  | 'cut'
  | 'copy'
  | 'format-painter'
  | 'font-family'
  | 'font-size'
  | 'bold'
  | 'italic'
  | 'underline'
  | 'borders'
  | 'font-color'
  | 'fill-color'
  | 'align-left'
  | 'align-center'
  | 'align-right'
  | 'decrease-indent'
  | 'increase-indent'
  | 'wrap-text'
  | 'merge-center'
  | 'clear-formats'
  | 'conditional-formatting'
  | 'cell-styles'
  | 'format-as-table'
  | 'number-format'
  | 'accounting-format'
  | 'percent-style'
  | 'comma-style'
  | 'increase-decimal'
  | 'decrease-decimal'
  | 'insert-cells'
  | 'delete-cells'
  | 'clear-contents'
  | 'format-cells'
  | 'autosum'
  | 'sort-filter'
  | 'find'
  | 'replace'
  | 'connect-data'
  | 'cfo-core'
  | 'import-data'
  | 'import-excel-workbook'
  | 'export-excel-workbook'
  | 'query-builder'
  | 'insert-table'
  | 'resize-table'
  | 'rename-table'
  | 'toggle-table-header-row'
  | 'toggle-table-filter-buttons'
  | 'toggle-table-total-row'
  | 'delete-table'
  | 'insert-chart'
  | 'insert-sparkline-line'
  | 'insert-sparkline-column'
  | 'insert-sparkline-win-loss'
  | 'clear-sparklines'
  | 'insert-pivot'
  | 'hyperlink'
  | 'open-hyperlink'
  | 'remove-hyperlink'
  | 'insert-function'
  | 'name-manager'
  | 'define-name'
  | 'use-in-formula'
  | 'create-from-selection'
  | 'paste-names'
  | 'apply-names'
  | 'calculation-options'
  | 'refresh-all'
  | 'data-validation'
  | 'circle-invalid-data'
  | 'clear-validation-circles'
  | 'remove-duplicates'
  | 'freeze-panes'
  | 'freeze-top-row'
  | 'freeze-first-column'
  | 'unfreeze-panes'
  | 'gridlines'
  | 'zoom'
  | 'fullscreen'
  | 'status-average'
  | 'status-count'
  | 'status-numerical-count'
  | 'status-sum'
  | 'comments'
  | 'clear-comment'
  | 'share'
  | 'protect-cells'
  | 'clear-cell-protection'
  | 'protect-sheet'
  | 'record-action'
  | 'creator-home'
  | 'import-workbook'
  | 'save-workbook'
  | 'delete-workbook'
  | 'creator-undo'
  | 'creator-redo'
  | 'developer-tools'
  | 'ai-panel'
  | 'map-background'
  | 'map-layers'
  | 'map-options'
  | 'add-visualization'
  | 'dashboard-actions'

export type SpreadsheetCommand = {
  id: SpreadsheetCommandId
  label: string
  shortLabel?: string
  description: string
  icon: string
  shortcut?: string
  implemented: boolean
  menuOnly?: boolean
  swatch?: 'font' | 'fill'
  defaultState?: SpreadsheetCommandState
}

export type RibbonGroup = {
  id: string
  label: string
  commands: SpreadsheetCommandId[]
}

export type RibbonTab = {
  id: 'file' | 'home' | 'insert' | 'formulas' | 'data' | 'review' | 'view' | 'automate' | 'visualize'
  label: string
  groups: RibbonGroup[]
}

export type SpreadsheetCommandContext = {
  hasSelection: boolean
  currentFormat?: CellFormat
  streamerAttached?: boolean
  localPreviewAttached?: boolean
  selectionKind?: 'cell' | 'row' | 'column' | 'all'
  hasStructuralActions?: boolean
  hasTableSelection?: boolean
  hasViewActions?: boolean
  gridlinesVisible?: boolean
  frozenPaneRows?: number
  frozenPaneCols?: number
  zoomPercent?: number
  formulaMode?: 'ready' | 'enter' | 'edit' | 'point'
  sheetProtection?: {
    enabled?: boolean
    allow_format_cells?: boolean
    allow_resize_rows?: boolean
    allow_resize_columns?: boolean
    allow_insert_rows?: boolean
    allow_insert_columns?: boolean
    allow_delete_rows?: boolean
    allow_delete_columns?: boolean
  } | null
  commandOverrides?: Partial<Record<SpreadsheetCommandId, SpreadsheetCommandState>>
}

export const SPREADSHEET_COMMANDS: Record<SpreadsheetCommandId, SpreadsheetCommand> = {
  undo: {
    id: 'undo',
    label: 'Undo',
    description: 'Undo the last spreadsheet edit.',
    icon: 'undo',
    shortcut: 'Ctrl+Z',
    implemented: true,
  },
  redo: {
    id: 'redo',
    label: 'Redo',
    description: 'Redo the last undone spreadsheet edit.',
    icon: 'redo',
    shortcut: 'Ctrl+Y',
    implemented: true,
  },
  save: {
    id: 'save',
    label: 'Save',
    description: 'Workbook autosaves through DocumentSession.',
    icon: 'save',
    implemented: false,
    defaultState: { enabled: false },
  },
  paste: {
    id: 'paste',
    label: 'Paste',
    description: 'Paste clipboard data into the active selection.',
    icon: 'paste',
    shortcut: 'Ctrl+V',
    implemented: true,
  },
  'paste-values': {
    id: 'paste-values',
    label: 'Values',
    description: 'Paste computed values from the last copied range.',
    icon: 'paste-values',
    implemented: true,
    menuOnly: true,
  },
  'paste-formulas': {
    id: 'paste-formulas',
    label: 'Formulas',
    description: 'Paste formulas and raw inputs from the last copied range.',
    icon: 'paste-formulas',
    implemented: true,
    menuOnly: true,
  },
  'paste-formats': {
    id: 'paste-formats',
    label: 'Formats',
    description: 'Paste formatting from the last copied range.',
    icon: 'paste-formats',
    implemented: true,
    menuOnly: true,
  },
  'paste-transpose': {
    id: 'paste-transpose',
    label: 'Transpose',
    description: 'Paste the last copied range with rows and columns swapped.',
    icon: 'paste-transpose',
    implemented: true,
    menuOnly: true,
  },
  'fill-copy-cells': {
    id: 'fill-copy-cells',
    label: 'Copy Cells',
    description: 'Reapply the last fill-handle action by copying source cells instead of extending a series.',
    icon: 'copy',
    implemented: true,
    menuOnly: true,
  },
  'fill-series': {
    id: 'fill-series',
    label: 'Fill Series',
    description: 'Reapply the last fill-handle action by continuing a detected numeric, date, weekday, month, or text-number series.',
    icon: 'rows',
    implemented: true,
    menuOnly: true,
  },
  'fill-days': {
    id: 'fill-days',
    label: 'Fill Days',
    description: 'Reapply the last fill-handle action by extending date values by calendar days.',
    icon: 'calendar',
    implemented: true,
    menuOnly: true,
  },
  'fill-weekdays': {
    id: 'fill-weekdays',
    label: 'Fill Weekdays',
    description: 'Reapply the last fill-handle action by extending date values through weekdays only.',
    icon: 'calendar',
    implemented: true,
    menuOnly: true,
  },
  'fill-months': {
    id: 'fill-months',
    label: 'Fill Months',
    description: 'Reapply the last fill-handle action by extending dates or month names by month.',
    icon: 'calendar',
    implemented: true,
    menuOnly: true,
  },
  'fill-years': {
    id: 'fill-years',
    label: 'Fill Years',
    description: 'Reapply the last fill-handle action by extending date values by year.',
    icon: 'calendar',
    implemented: true,
    menuOnly: true,
  },
  cut: {
    id: 'cut',
    label: 'Cut',
    description: 'Cut the current selection.',
    icon: 'scissors',
    shortcut: 'Ctrl+X',
    implemented: true,
  },
  copy: {
    id: 'copy',
    label: 'Copy',
    description: 'Copy the current selection.',
    icon: 'copy',
    shortcut: 'Ctrl+C',
    implemented: true,
  },
  'format-painter': {
    id: 'format-painter',
    label: 'Format Painter',
    shortLabel: 'Painter',
    description: 'Copy formatting from the selected cell.',
    icon: 'paintbrush',
    implemented: true,
  },
  'font-family': {
    id: 'font-family',
    label: 'Font',
    description: 'Choose the selected cells font family.',
    icon: 'excel-font-family',
    implemented: true,
  },
  'font-size': {
    id: 'font-size',
    label: 'Font Size',
    shortLabel: 'Size',
    description: 'Choose the selected cells font size.',
    icon: 'excel-font-size',
    implemented: true,
  },
  bold: {
    id: 'bold',
    label: 'Bold',
    shortLabel: 'B',
    description: 'Apply bold formatting.',
    icon: 'bold',
    shortcut: 'Ctrl+B',
    implemented: true,
  },
  italic: {
    id: 'italic',
    label: 'Italic',
    shortLabel: 'I',
    description: 'Apply italic formatting.',
    icon: 'italic',
    shortcut: 'Ctrl+I',
    implemented: true,
  },
  underline: {
    id: 'underline',
    label: 'Underline',
    shortLabel: 'U',
    description: 'Apply underline formatting.',
    icon: 'underline',
    shortcut: 'Ctrl+U',
    implemented: true,
  },
  borders: {
    id: 'borders',
    label: 'Borders',
    description: 'Apply borders to the selected cells.',
    icon: 'excel-borders',
    implemented: true,
  },
  'font-color': {
    id: 'font-color',
    label: 'Font Color',
    shortLabel: 'A',
    description: 'Set the selected text color.',
    icon: 'excel-font-color',
    implemented: true,
    swatch: 'font',
  },
  'fill-color': {
    id: 'fill-color',
    label: 'Fill Color',
    description: 'Set the selected cell fill color.',
    icon: 'excel-fill-color',
    implemented: true,
    swatch: 'fill',
  },
  'align-left': {
    id: 'align-left',
    label: 'Align Left',
    description: 'Left-align selected cells.',
    icon: 'align-left',
    implemented: true,
  },
  'align-center': {
    id: 'align-center',
    label: 'Align Center',
    description: 'Center-align selected cells.',
    icon: 'align-center',
    implemented: true,
  },
  'align-right': {
    id: 'align-right',
    label: 'Align Right',
    description: 'Right-align selected cells.',
    icon: 'align-right',
    implemented: true,
  },
  'decrease-indent': {
    id: 'decrease-indent',
    label: 'Decrease Indent',
    shortLabel: '<',
    description: 'Move selected cell text closer to the cell edge.',
    icon: 'decrease-indent',
    implemented: true,
  },
  'increase-indent': {
    id: 'increase-indent',
    label: 'Increase Indent',
    shortLabel: '>',
    description: 'Move selected cell text farther from the cell edge.',
    icon: 'increase-indent',
    implemented: true,
  },
  'wrap-text': {
    id: 'wrap-text',
    label: 'Wrap Text',
    shortLabel: 'Wrap',
    description: 'Wrap text inside selected cells.',
    icon: 'wrap-text',
    implemented: true,
  },
  'merge-center': {
    id: 'merge-center',
    label: 'Merge & Center',
    description: 'Merge selected cells and center content.',
    icon: 'excel-merge-center',
    implemented: true,
  },
  'clear-formats': {
    id: 'clear-formats',
    label: 'Clear Formats',
    shortLabel: 'Clear',
    description: 'Clear formatting from the selected cells without changing values or formulas.',
    icon: 'excel-clear-formats',
    implemented: true,
  },
  'conditional-formatting': {
    id: 'conditional-formatting',
    label: 'Conditional Formatting',
    shortLabel: 'Conditional',
    description: 'Create, clear, or manage conditional formatting rules.',
    icon: 'excel-conditional-formatting',
    implemented: true,
  },
  'cell-styles': {
    id: 'cell-styles',
    label: 'Cell Styles',
    shortLabel: 'Styles',
    description: 'Apply a built-in cell style to the selection.',
    icon: 'excel-cell-styles',
    implemented: true,
  },
  'format-as-table': {
    id: 'format-as-table',
    label: 'Format as Table',
    shortLabel: 'Table Style',
    description: 'Apply a table-style format to the selected range without creating table metadata.',
    icon: 'excel-format-as-table',
    implemented: true,
  },
  'number-format': {
    id: 'number-format',
    label: 'Number Format',
    shortLabel: 'General',
    description: 'Choose a number format.',
    icon: 'excel-number-format',
    implemented: true,
  },
  'accounting-format': {
    id: 'accounting-format',
    label: 'Accounting',
    shortLabel: '$',
    description: 'Apply accounting number format.',
    icon: 'excel-accounting-format',
    implemented: true,
  },
  'percent-style': {
    id: 'percent-style',
    label: 'Percent Style',
    shortLabel: '%',
    description: 'Apply percentage number format.',
    icon: 'excel-percent-style',
    implemented: true,
  },
  'comma-style': {
    id: 'comma-style',
    label: 'Comma Style',
    shortLabel: ',',
    description: 'Apply comma number format.',
    icon: 'excel-comma-style',
    implemented: true,
  },
  'increase-decimal': {
    id: 'increase-decimal',
    label: 'Increase Decimal',
    shortLabel: '.0',
    description: 'Show more decimal places.',
    icon: 'excel-increase-decimal',
    implemented: true,
  },
  'decrease-decimal': {
    id: 'decrease-decimal',
    label: 'Decrease Decimal',
    shortLabel: '.00',
    description: 'Show fewer decimal places.',
    icon: 'excel-decrease-decimal',
    implemented: true,
  },
  'insert-cells': {
    id: 'insert-cells',
    label: 'Insert',
    description: 'Insert cells, rows, columns, or sheets.',
    icon: 'excel-insert-cells',
    implemented: true,
  },
  'delete-cells': {
    id: 'delete-cells',
    label: 'Delete',
    description: 'Delete cells, rows, columns, or sheets.',
    icon: 'excel-delete-cells',
    implemented: true,
  },
  'clear-contents': {
    id: 'clear-contents',
    label: 'Clear Contents',
    description: 'Clear values and formulas from the selected cells.',
    icon: 'excel-clear-contents',
    implemented: true,
  },
  'format-cells': {
    id: 'format-cells',
    label: 'Format',
    description: 'Open cell formatting options.',
    icon: 'excel-format-cells',
    shortcut: 'Ctrl+1',
    implemented: true,
  },
  autosum: {
    id: 'autosum',
    label: 'AutoSum',
    description: 'Insert a SUM formula.',
    icon: 'sigma',
    shortcut: 'Alt+=',
    implemented: true,
  },
  'sort-filter': {
    id: 'sort-filter',
    label: 'Sort & Filter',
    description: 'Sort and filter the selected range.',
    icon: 'filter',
    implemented: true,
  },
  find: {
    id: 'find',
    label: 'Find',
    description: 'Find text in the sheet.',
    icon: 'search',
    shortcut: 'Ctrl+F',
    implemented: true,
  },
  replace: {
    id: 'replace',
    label: 'Replace',
    description: 'Find and replace cell text in the active sheet.',
    icon: 'replace',
    shortcut: 'Ctrl+H',
    implemented: true,
  },
  'connect-data': {
    id: 'connect-data',
    label: 'Connect Data',
    description: 'Connect this workbook to a datasource.',
    icon: 'database',
    implemented: true,
  },
  'cfo-core': {
    id: 'cfo-core',
    label: 'CFO Core',
    description: 'Open QuickBooks financial statement layouts and reporting tools.',
    icon: 'layout-dashboard',
    implemented: true,
  },
  'import-data': {
    id: 'import-data',
    label: 'Import Data',
    shortLabel: 'Import',
    description: 'Import tabular data into the active sheet.',
    icon: 'upload',
    implemented: true,
  },
  'import-excel-workbook': {
    id: 'import-excel-workbook',
    label: 'Import Excel',
    shortLabel: 'Import Excel',
    description: 'Import an .xlsx workbook through the spreadsheet engine.',
    icon: 'upload',
    implemented: true,
  },
  'export-excel-workbook': {
    id: 'export-excel-workbook',
    label: 'Export Excel',
    shortLabel: 'Export Excel',
    description: 'Export the current workbook as an .xlsx file.',
    icon: 'download',
    implemented: true,
  },
  'query-builder': {
    id: 'query-builder',
    label: 'Query Builder',
    shortLabel: 'Query',
    description: 'Open FuseGrid query tools.',
    icon: 'database',
    implemented: true,
  },
  'insert-table': {
    id: 'insert-table',
    label: 'Table',
    description: 'Create an engine-owned table from the selected range.',
    icon: 'insert-table',
    implemented: true,
  },
  'resize-table': {
    id: 'resize-table',
    label: 'Resize Table to Selection',
    shortLabel: 'Resize Table',
    description: 'Resize the engine-owned table to the current selection.',
    icon: 'resize-table',
    implemented: true,
    menuOnly: true,
  },
  'rename-table': {
    id: 'rename-table',
    label: 'Rename Table',
    description: 'Rename the selected engine-owned table.',
    icon: 'rename-table',
    implemented: true,
    menuOnly: true,
  },
  'toggle-table-header-row': {
    id: 'toggle-table-header-row',
    label: 'Toggle Header Row',
    shortLabel: 'Header Row',
    description: 'Show or hide the engine-owned table header row.',
    icon: 'toggle-table-header-row',
    implemented: true,
    menuOnly: true,
  },
  'toggle-table-filter-buttons': {
    id: 'toggle-table-filter-buttons',
    label: 'Toggle Filter Buttons',
    shortLabel: 'Filter Buttons',
    description: 'Show or hide engine-owned filter buttons for the selected table.',
    icon: 'toggle-table-filter-buttons',
    implemented: true,
    menuOnly: true,
  },
  'toggle-table-total-row': {
    id: 'toggle-table-total-row',
    label: 'Toggle Total Row',
    shortLabel: 'Total Row',
    description: 'Show or hide the engine-owned total row for the selected table.',
    icon: 'toggle-table-total-row',
    implemented: true,
    menuOnly: true,
  },
  'delete-table': {
    id: 'delete-table',
    label: 'Delete Table',
    description: 'Delete the engine-owned table metadata while leaving cells in place.',
    icon: 'delete-table',
    implemented: true,
    menuOnly: true,
  },
  'insert-chart': {
    id: 'insert-chart',
    label: 'Chart',
    description: 'Create a chart artifact from the selected range.',
    icon: 'chart',
    implemented: true,
  },
  'insert-sparkline-line': {
    id: 'insert-sparkline-line',
    label: 'Line',
    shortLabel: 'Line',
    description: 'Insert Excel-compatible line sparklines for the selected data.',
    icon: 'insert-sparkline-line',
    implemented: true,
  },
  'insert-sparkline-column': {
    id: 'insert-sparkline-column',
    label: 'Column',
    shortLabel: 'Column',
    description: 'Insert Excel-compatible column sparklines for the selected data.',
    icon: 'insert-sparkline-column',
    implemented: true,
  },
  'insert-sparkline-win-loss': {
    id: 'insert-sparkline-win-loss',
    label: 'Win/Loss',
    shortLabel: 'Win/Loss',
    description: 'Insert Excel-compatible win/loss sparklines for the selected data.',
    icon: 'insert-sparkline-win-loss',
    implemented: true,
  },
  'clear-sparklines': {
    id: 'clear-sparklines',
    label: 'Clear',
    shortLabel: 'Clear',
    description: 'Clear sparklines from the selected cells.',
    icon: 'eraser',
    implemented: true,
  },
  'insert-pivot': {
    id: 'insert-pivot',
    label: 'PivotTable',
    description: 'Create a pivot artifact from the selected range.',
    icon: 'pivot',
    implemented: true,
  },
  hyperlink: {
    id: 'hyperlink',
    label: 'Link',
    description: 'Add or edit a hyperlink on the active cell.',
    icon: 'link',
    shortcut: 'Ctrl+K',
    implemented: true,
  },
  'open-hyperlink': {
    id: 'open-hyperlink',
    label: 'Open Link',
    description: 'Open the hyperlink stored on the active cell.',
    icon: 'open-hyperlink',
    implemented: true,
    menuOnly: true,
  },
  'remove-hyperlink': {
    id: 'remove-hyperlink',
    label: 'Remove Link',
    description: 'Remove the hyperlink stored on the active cell.',
    icon: 'unlink',
    implemented: true,
    menuOnly: true,
  },
  'insert-function': {
    id: 'insert-function',
    label: 'Insert Function',
    description: 'Search for a formula function.',
    icon: 'function',
    shortcut: 'Shift+F3',
    implemented: true,
  },
  'name-manager': {
    id: 'name-manager',
    label: 'Name Manager',
    description: 'Create and manage defined names.',
    icon: 'tag',
    implemented: true,
  },
  'define-name': {
    id: 'define-name',
    label: 'Define Name',
    description: 'Create a defined name for the current selection.',
    icon: 'plus',
    implemented: true,
  },
  'use-in-formula': {
    id: 'use-in-formula',
    label: 'Use in Formula',
    description: 'Insert a defined name or structured table reference into the current formula.',
    icon: 'function',
    implemented: true,
  },
  'create-from-selection': {
    id: 'create-from-selection',
    label: 'Create from Selection',
    shortLabel: 'Create Names',
    description: 'Create defined names from labels in the selected range.',
    icon: 'columns',
    implemented: true,
  },
  'paste-names': {
    id: 'paste-names',
    label: 'Paste Names',
    description: 'Paste defined names and their references into the worksheet.',
    icon: 'paste-names',
    implemented: true,
  },
  'apply-names': {
    id: 'apply-names',
    label: 'Apply Names',
    description: 'Replace matching formula references with defined names.',
    icon: 'replace',
    implemented: true,
  },
  'calculation-options': {
    id: 'calculation-options',
    label: 'Calculation Options',
    shortLabel: 'Calculation',
    description: 'Configure workbook calculation.',
    icon: 'calculator',
    implemented: false,
  },
  'refresh-all': {
    id: 'refresh-all',
    label: 'Refresh All',
    description: 'Refresh external workbook data.',
    icon: 'refresh',
    implemented: false,
  },
  'data-validation': {
    id: 'data-validation',
    label: 'Data Validation',
    description: 'Restrict allowed input in cells.',
    icon: 'check-circle',
    implemented: true,
  },
  'circle-invalid-data': {
    id: 'circle-invalid-data',
    label: 'Circle Invalid Data',
    shortLabel: 'Circle Invalid',
    description: 'Find and mark cells that violate data validation rules.',
    icon: 'circle-invalid-data',
    implemented: true,
  },
  'clear-validation-circles': {
    id: 'clear-validation-circles',
    label: 'Clear Validation Circles',
    shortLabel: 'Clear Circles',
    description: 'Remove invalid-data review markers from the grid.',
    icon: 'clear-validation-circles',
    implemented: true,
  },
  'remove-duplicates': {
    id: 'remove-duplicates',
    label: 'Remove Duplicates',
    shortLabel: 'Duplicates',
    description: 'Remove duplicate rows.',
    icon: 'rows',
    implemented: true,
  },
  'freeze-panes': {
    id: 'freeze-panes',
    label: 'Freeze Panes',
    description: 'Keep rows or columns visible while scrolling.',
    icon: 'panel-top',
    implemented: true,
  },
  'freeze-top-row': {
    id: 'freeze-top-row',
    label: 'Freeze Top Row',
    shortLabel: 'Top Row',
    description: 'Keep the first row visible while scrolling.',
    icon: 'rows',
    implemented: true,
  },
  'freeze-first-column': {
    id: 'freeze-first-column',
    label: 'Freeze First Column',
    shortLabel: 'First Col',
    description: 'Keep the first column visible while scrolling.',
    icon: 'columns',
    implemented: true,
  },
  'unfreeze-panes': {
    id: 'unfreeze-panes',
    label: 'Unfreeze Panes',
    shortLabel: 'Unfreeze',
    description: 'Clear frozen rows and columns.',
    icon: 'grid',
    implemented: true,
  },
  gridlines: {
    id: 'gridlines',
    label: 'Gridlines',
    description: 'Show or hide sheet gridlines.',
    icon: 'grid',
    implemented: true,
    defaultState: { enabled: true, active: true },
  },
  zoom: {
    id: 'zoom',
    label: 'Zoom',
    description: 'Change workbook zoom.',
    icon: 'zoom-in',
    implemented: true,
  },
  fullscreen: {
    id: 'fullscreen',
    label: 'Full Screen',
    description: 'Enter full-screen workbook mode.',
    icon: 'maximize',
    implemented: true,
  },
  'status-average': {
    id: 'status-average',
    label: 'Average',
    description: 'Show or hide the average of numeric cells in the selected range.',
    icon: 'calculator',
    implemented: true,
    menuOnly: true,
    defaultState: { enabled: true, active: true },
  },
  'status-count': {
    id: 'status-count',
    label: 'Count',
    description: 'Show or hide the count of nonblank cells in the selected range.',
    icon: 'hash',
    implemented: true,
    menuOnly: true,
    defaultState: { enabled: true, active: true },
  },
  'status-numerical-count': {
    id: 'status-numerical-count',
    label: 'Numerical Count',
    description: 'Show or hide the count of numeric cells in the selected range.',
    icon: 'hash',
    implemented: true,
    menuOnly: true,
    defaultState: { enabled: true, active: true },
  },
  'status-sum': {
    id: 'status-sum',
    label: 'Sum',
    description: 'Show or hide the sum of numeric cells in the selected range.',
    icon: 'sigma',
    implemented: true,
    menuOnly: true,
    defaultState: { enabled: true, active: true },
  },
  comments: {
    id: 'comments',
    label: 'Comments',
    description: 'Open the comments pane for engine-backed cell comments.',
    icon: 'message',
    implemented: true,
  },
  'clear-comment': {
    id: 'clear-comment',
    label: 'Clear Comment',
    description: 'Remove the comment stored on the active cell.',
    icon: 'message-x',
    implemented: true,
    menuOnly: true,
  },
  share: {
    id: 'share',
    label: 'Share',
    description: 'Share the workbook.',
    icon: 'share',
    implemented: false,
  },
  'protect-cells': {
    id: 'protect-cells',
    label: 'Protect Cells',
    shortLabel: 'Cells',
    description: 'Lock or unlock the selected cell range.',
    icon: 'lock',
    implemented: true,
  },
  'clear-cell-protection': {
    id: 'clear-cell-protection',
    label: 'Use Default Protection',
    description: 'Clear the active cell protection override so sheet defaults apply.',
    icon: 'unlock',
    implemented: true,
    menuOnly: true,
  },
  'protect-sheet': {
    id: 'protect-sheet',
    label: 'Protect Sheet',
    shortLabel: 'Sheet',
    description: 'Protect or unprotect the active sheet.',
    icon: 'lock',
    implemented: true,
  },
  'record-action': {
    id: 'record-action',
    label: 'Record Action',
    description: 'Record an automation step.',
    icon: 'record',
    implemented: false,
  },
  'creator-home': {
    id: 'creator-home',
    label: 'Creator Home',
    shortLabel: 'Home',
    description: 'Return to Creator Home.',
    icon: 'home',
    implemented: true,
  },
  'import-workbook': {
    id: 'import-workbook',
    label: 'Import',
    description: 'Import sheets and vizzes into this workbook.',
    icon: 'download',
    implemented: true,
  },
  'save-workbook': {
    id: 'save-workbook',
    label: 'Save Everything',
    shortLabel: 'Save All',
    description: 'Save workbook metadata and datasource extracts into the package.',
    icon: 'save',
    implemented: true,
  },
  'delete-workbook': {
    id: 'delete-workbook',
    label: 'Delete Workbook',
    shortLabel: 'Delete',
    description: 'Delete the active workbook.',
    icon: 'trash',
    implemented: true,
  },
  'creator-undo': {
    id: 'creator-undo',
    label: 'Undo',
    description: 'Undo the last Creator authoring action.',
    icon: 'undo',
    shortcut: 'Ctrl+Z',
    implemented: true,
  },
  'creator-redo': {
    id: 'creator-redo',
    label: 'Redo',
    description: 'Redo the last Creator authoring action.',
    icon: 'redo',
    shortcut: 'Ctrl+Y',
    implemented: true,
  },
  'developer-tools': {
    id: 'developer-tools',
    label: 'Developer',
    description: 'Show or hide Creator developer diagnostics.',
    icon: 'code',
    implemented: true,
  },
  'ai-panel': {
    id: 'ai-panel',
    label: 'AI Panel',
    shortLabel: 'AI',
    description: 'Show or hide the Creator AI panel.',
    icon: 'sparkles',
    implemented: true,
  },
  'map-layers': {
    id: 'map-layers',
    label: 'Map Layers',
    shortLabel: 'Layers',
    description: 'Open map layer controls for map visualizations.',
    icon: 'layers',
    implemented: true,
  },
  'map-background': {
    id: 'map-background',
    label: 'Background Map',
    shortLabel: 'Background',
    description: 'Choose the map background style for map visualizations.',
    icon: 'map',
    implemented: true,
  },
  'map-options': {
    id: 'map-options',
    label: 'Map Options',
    shortLabel: 'Map',
    description: 'Open map options for map visualizations.',
    icon: 'map',
    implemented: true,
  },
  'add-visualization': {
    id: 'add-visualization',
    label: 'Add Viz',
    description: 'Add a new visualization to the active dashboard-backed sheet.',
    icon: 'add-visualization',
    implemented: true,
  },
  'dashboard-actions': {
    id: 'dashboard-actions',
    label: 'Actions',
    description: 'Configure workbook dashboard actions.',
    icon: 'layout-dashboard',
    implemented: true,
  },
}

export const EXCEL_RIBBON_TABS: RibbonTab[] = [
  {
    id: 'file',
    label: 'File',
    groups: [
      { id: 'file-primary', label: 'Workbook', commands: ['save', 'save-workbook', 'share'] },
    ],
  },
  {
    id: 'home',
    label: 'Home',
    groups: [
      { id: 'history', label: 'History', commands: ['undo', 'redo'] },
      { id: 'clipboard', label: 'Clipboard', commands: ['paste', 'cut', 'copy', 'format-painter'] },
      { id: 'font', label: 'Font', commands: ['font-family', 'font-size', 'bold', 'italic', 'underline', 'borders', 'font-color', 'fill-color'] },
      { id: 'alignment', label: 'Alignment', commands: ['align-left', 'align-center', 'align-right', 'decrease-indent', 'increase-indent', 'wrap-text', 'merge-center'] },
      { id: 'number', label: 'Number', commands: ['number-format', 'accounting-format', 'percent-style', 'comma-style', 'increase-decimal', 'decrease-decimal'] },
      { id: 'styles', label: 'Styles', commands: ['conditional-formatting', 'cell-styles', 'format-as-table'] },
      { id: 'cells', label: 'Cells', commands: ['insert-cells', 'delete-cells', 'format-cells', 'clear-formats'] },
      { id: 'editing', label: 'Editing', commands: ['autosum', 'sort-filter', 'find', 'replace'] },
    ],
  },
  {
    id: 'insert',
    label: 'Insert',
    groups: [
      { id: 'tables', label: 'Tables', commands: ['insert-table', 'insert-pivot'] },
      { id: 'illustrations', label: 'Visuals', commands: ['insert-chart'] },
      { id: 'sparklines', label: 'Sparklines', commands: ['insert-sparkline-line', 'insert-sparkline-column', 'insert-sparkline-win-loss', 'clear-sparklines'] },
      { id: 'links', label: 'Links', commands: ['hyperlink'] },
      { id: 'imports', label: 'Data', commands: ['connect-data', 'import-data'] },
    ],
  },
  {
    id: 'formulas',
    label: 'Formulas',
    groups: [
      { id: 'function-library', label: 'Function Library', commands: ['insert-function', 'autosum'] },
      { id: 'defined-names', label: 'Defined Names', commands: ['name-manager', 'define-name', 'use-in-formula', 'create-from-selection', 'paste-names', 'apply-names'] },
      { id: 'calculation', label: 'Calculation', commands: ['calculation-options'] },
    ],
  },
  {
    id: 'data',
    label: 'Data',
    groups: [
      { id: 'queries', label: 'Get & Transform', commands: ['connect-data', 'cfo-core', 'import-data', 'import-excel-workbook', 'export-excel-workbook', 'refresh-all'] },
      { id: 'data-tools', label: 'Data Tools', commands: ['sort-filter', 'data-validation', 'circle-invalid-data', 'clear-validation-circles', 'remove-duplicates'] },
    ],
  },
  {
    id: 'review',
    label: 'Review',
    groups: [
      { id: 'review-comments', label: 'Comments', commands: ['comments'] },
      { id: 'protection', label: 'Protect', commands: ['protect-cells', 'protect-sheet'] },
    ],
  },
  {
    id: 'view',
    label: 'View',
    groups: [
      { id: 'sheet-view', label: 'Sheet View', commands: ['freeze-panes', 'freeze-top-row', 'freeze-first-column', 'unfreeze-panes', 'gridlines'] },
      { id: 'zoom', label: 'Zoom', commands: ['zoom', 'fullscreen'] },
    ],
  },
  {
    id: 'automate',
    label: 'Automate',
    groups: [
      { id: 'automation', label: 'Actions', commands: ['record-action'] },
    ],
  },
  {
    id: 'visualize',
    label: 'Visualize',
    groups: [
      { id: 'creator-workbook', label: 'Creator', commands: ['creator-home', 'import-workbook', 'delete-workbook'] },
      { id: 'visual-tools', label: 'Visuals', commands: ['add-visualization', 'dashboard-actions', 'map-background', 'map-layers', 'map-options'] },
      { id: 'creator-panels', label: 'Panels', commands: ['developer-tools', 'ai-panel'] },
    ],
  },
]

function styleSection(format: CellFormat | undefined, key: keyof CellFormat): Record<string, unknown> {
  const value = format?.[key]
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}
}

function booleanStyle(format: CellFormat | undefined, mirrorKey: keyof CellFormat, sectionKey: keyof CellFormat, sectionField: string): boolean {
  const mirror = format?.[mirrorKey]
  if (typeof mirror === 'boolean') return mirror
  return styleSection(format, sectionKey)[sectionField] === true
}

const VISUALIZE_COMMANDS = new Set<SpreadsheetCommandId>([
  'creator-home',
  'import-workbook',
  'save-workbook',
  'delete-workbook',
  'creator-undo',
  'creator-redo',
  'developer-tools',
  'ai-panel',
  'map-background',
  'map-layers',
  'map-options',
  'add-visualization',
  'dashboard-actions',
])

const SHEET_PROTECTION_FORMAT_COMMANDS = new Set<SpreadsheetCommandId>([
  'format-painter',
  'font-family',
  'font-size',
  'bold',
  'italic',
  'underline',
  'borders',
  'font-color',
  'fill-color',
  'align-left',
  'align-center',
  'align-right',
  'decrease-indent',
  'increase-indent',
  'wrap-text',
  'merge-center',
  'clear-formats',
  'conditional-formatting',
  'insert-sparkline-line',
  'insert-sparkline-column',
  'insert-sparkline-win-loss',
  'clear-sparklines',
  'cell-styles',
  'format-as-table',
  'number-format',
  'accounting-format',
  'percent-style',
  'comma-style',
  'increase-decimal',
  'decrease-decimal',
  'format-cells',
])

export function getSpreadsheetCommand(id: SpreadsheetCommandId): SpreadsheetCommand {
  return SPREADSHEET_COMMANDS[id]
}

export function getCommandState(
  command: SpreadsheetCommand,
  context: SpreadsheetCommandContext,
): SpreadsheetCommandState {
  const base = command.defaultState ?? { enabled: true }
  const selectionRequired = new Set<SpreadsheetCommandId>([
    'paste',
    'paste-values',
    'paste-formulas',
    'paste-formats',
    'paste-transpose',
    'fill-copy-cells',
    'fill-series',
    'fill-days',
    'fill-weekdays',
    'fill-months',
    'fill-years',
    'cut',
    'copy',
    'format-painter',
    'font-family',
    'font-size',
    'bold',
    'italic',
    'underline',
    'borders',
    'font-color',
    'fill-color',
    'align-left',
    'align-center',
    'align-right',
    'decrease-indent',
    'increase-indent',
    'wrap-text',
    'merge-center',
    'clear-formats',
    'conditional-formatting',
    'insert-chart',
    'insert-pivot',
    'insert-sparkline-line',
    'insert-sparkline-column',
    'insert-sparkline-win-loss',
    'clear-sparklines',
    'cell-styles',
    'format-as-table',
    'number-format',
    'accounting-format',
    'percent-style',
    'comma-style',
    'increase-decimal',
    'decrease-decimal',
    'clear-contents',
    'format-cells',
    'hyperlink',
    'open-hyperlink',
    'remove-hyperlink',
    'data-validation',
    'comments',
    'clear-comment',
    'protect-cells',
    'clear-cell-protection',
    'define-name',
    'use-in-formula',
    'create-from-selection',
    'paste-names',
    'apply-names',
    'insert-table',
    'resize-table',
    'rename-table',
    'toggle-table-header-row',
    'toggle-table-filter-buttons',
    'toggle-table-total-row',
    'delete-table',
    'autosum',
    'sort-filter',
    'remove-duplicates',
    'freeze-panes',
  ])
  const streamerRequired = new Set<SpreadsheetCommandId>([
    'undo',
    'redo',
    'paste',
    'paste-values',
    'paste-formulas',
    'paste-formats',
    'paste-transpose',
    'fill-copy-cells',
    'fill-series',
    'fill-days',
    'fill-weekdays',
    'fill-months',
    'fill-years',
    'cut',
    'copy',
    'format-painter',
    'font-family',
    'font-size',
    'bold',
    'italic',
    'underline',
    'borders',
    'font-color',
    'fill-color',
    'align-left',
    'align-center',
    'align-right',
    'decrease-indent',
    'increase-indent',
    'wrap-text',
    'merge-center',
    'number-format',
    'accounting-format',
    'percent-style',
    'comma-style',
    'increase-decimal',
    'decrease-decimal',
    'clear-contents',
    'clear-formats',
    'conditional-formatting',
    'insert-chart',
    'insert-pivot',
    'insert-sparkline-line',
    'insert-sparkline-column',
    'insert-sparkline-win-loss',
    'clear-sparklines',
    'cell-styles',
    'format-as-table',
    'format-cells',
    'hyperlink',
    'open-hyperlink',
    'remove-hyperlink',
    'data-validation',
    'circle-invalid-data',
    'clear-validation-circles',
    'sort-filter',
    'remove-duplicates',
    'comments',
    'clear-comment',
    'protect-cells',
    'clear-cell-protection',
    'protect-sheet',
    'name-manager',
    'define-name',
    'use-in-formula',
    'create-from-selection',
    'paste-names',
    'apply-names',
    'insert-table',
    'resize-table',
    'rename-table',
    'toggle-table-header-row',
    'toggle-table-filter-buttons',
    'toggle-table-total-row',
    'delete-table',
    'freeze-panes',
    'freeze-top-row',
    'freeze-first-column',
    'unfreeze-panes',
  ])
  const localPreviewEligible = new Set<SpreadsheetCommandId>([
    'bold',
    'font-family',
    'font-size',
    'italic',
    'underline',
    'borders',
    'font-color',
    'fill-color',
    'align-left',
    'align-center',
    'align-right',
    'decrease-indent',
    'increase-indent',
    'wrap-text',
    'number-format',
    'accounting-format',
    'percent-style',
    'comma-style',
    'increase-decimal',
    'decrease-decimal',
    'cell-styles',
    'format-as-table',
    'clear-formats',
    'clear-contents',
    'format-cells',
    'freeze-panes',
    'freeze-top-row',
    'freeze-first-column',
    'unfreeze-panes',
  ])
  const paneRows = Math.max(0, Math.floor(Number(context.frozenPaneRows) || 0))
  const paneCols = Math.max(0, Math.floor(Number(context.frozenPaneCols) || 0))
  const enabled = Boolean(
    base.enabled !== false &&
    command.implemented &&
    (!selectionRequired.has(command.id) || context.hasSelection) &&
    (!streamerRequired.has(command.id) ||
      context.streamerAttached !== false ||
      (localPreviewEligible.has(command.id) && context.localPreviewAttached === true)),
  )
  const structuralRibbonCommand = command.id === 'insert-cells' || command.id === 'delete-cells'
  const sheetProtected = context.sheetProtection?.enabled === true
  const sheetProtection = context.sheetProtection ?? null
  const formatBlockedByProtection = sheetProtected &&
    sheetProtection?.allow_format_cells !== true &&
    SHEET_PROTECTION_FORMAT_COMMANDS.has(command.id)
  const structuralBlockedByProtection = (() => {
    if (!sheetProtected || !structuralRibbonCommand) return false
    if (command.id === 'insert-cells') {
      if (context.selectionKind === 'row') return sheetProtection?.allow_insert_rows !== true
      if (context.selectionKind === 'column') return sheetProtection?.allow_insert_columns !== true
      return false
    }
    if (command.id === 'delete-cells') {
      if (context.selectionKind === 'row') return sheetProtection?.allow_delete_rows !== true
      if (context.selectionKind === 'column') return sheetProtection?.allow_delete_columns !== true
    }
    return false
  })()
  const structuralEnabled = !structuralRibbonCommand || Boolean(
    context.hasStructuralActions &&
    (context.selectionKind === 'row' || context.selectionKind === 'column'),
  )
  const viewCommand = command.id === 'gridlines' ||
    command.id === 'zoom' ||
    command.id === 'freeze-panes' ||
    command.id === 'freeze-top-row' ||
    command.id === 'freeze-first-column' ||
    command.id === 'unfreeze-panes'
  const viewEnabled = !viewCommand || Boolean(context.hasViewActions)
  const visualizeEnabled = !VISUALIZE_COMMANDS.has(command.id) || Boolean(context.commandOverrides?.[command.id])
  const tableSelectionRequired = command.id === 'resize-table' ||
    command.id === 'rename-table' ||
    command.id === 'toggle-table-header-row' ||
    command.id === 'toggle-table-filter-buttons' ||
    command.id === 'toggle-table-total-row' ||
    command.id === 'delete-table'
  const tableSelectionEnabled = !tableSelectionRequired || context.hasTableSelection === true
  const unfreezeEnabled = command.id !== 'unfreeze-panes' || paneRows > 0 || paneCols > 0
  const alignment = styleSection(context.currentFormat, 'alignment')
  const align = context.currentFormat?.align ?? alignment.horizontal
  const active =
    command.id === 'bold' ? booleanStyle(context.currentFormat, 'bold', 'font', 'bold') :
      command.id === 'italic' ? booleanStyle(context.currentFormat, 'italic', 'font', 'italic') :
        command.id === 'underline' ? Boolean(context.currentFormat?.underline ?? styleSection(context.currentFormat, 'font').underline) :
          command.id === 'align-left' ? (!align || align === 'left') :
            command.id === 'align-center' ? align === 'center' :
            command.id === 'align-right' ? align === 'right' :
              command.id === 'wrap-text' ? Boolean(context.currentFormat?.wrapText || context.currentFormat?.alignment?.wrapText) :
                command.id === 'gridlines' ? context.gridlinesVisible !== false :
                  command.id === 'freeze-panes' ? paneRows > 0 || paneCols > 0 :
                    command.id === 'freeze-top-row' ? paneRows === 1 :
                      command.id === 'freeze-first-column' ? paneCols === 1 :
                        command.id === 'zoom' ? Math.round(Number(context.zoomPercent) || 100) !== 100 :
                          base.active
  const resolved: SpreadsheetCommandState = {
    visible: base.visible !== false,
    enabled: enabled &&
      structuralEnabled &&
      viewEnabled &&
      visualizeEnabled &&
      tableSelectionEnabled &&
      unfreezeEnabled &&
      !formatBlockedByProtection &&
      !structuralBlockedByProtection,
    active,
  }
  if (formatBlockedByProtection) {
    resolved.disabledReason = 'Protected sheet does not allow formatting cells'
  } else if (structuralBlockedByProtection) {
    const target = context.selectionKind === 'row' ? 'rows' : 'columns'
    const verb = command.id === 'insert-cells' ? 'inserting' : 'deleting'
    resolved.disabledReason = `Protected sheet does not allow ${verb} ${target}`
  } else if (!tableSelectionEnabled) {
    resolved.disabledReason = 'Select a cell inside an engine-owned table'
  }
  const override = context.commandOverrides?.[command.id]
  if (!override) return resolved
  return {
    visible: resolved.visible && override.visible !== false,
    enabled: resolved.enabled && override.enabled !== false,
    active: override.active ?? resolved.active,
    disabledReason: override.disabledReason ?? resolved.disabledReason,
  }
}
